import React, { createContext, memo, useContext, useMemo } from 'react';
import cx from 'classnames';
import { NON_PROTOCOL, SITUATIONAL } from 'constants/encounter';
import { CANCELED, CLOSED } from 'constants/ssuStatuses';
import { MANAGE_PRE_SCREEN_WORK_LIST, MANAGE_PROTOCOL_ENCOUNTER_DATA } from 'constants/userOperations';
import { ROLE_SYSTEM_ADMINISTRATOR } from 'constants/userRoles';
import { last } from 'lodash/array';
import { reject } from 'lodash/collection';
import { userHasAccessTo, userHasRole } from 'services/auth';

import { usePatientInfo } from '../../../../PatientInfoContext';

import CardHeaderLeftDefault from './templates/card-headers/CardHeaderLeftDefault';
import CardHeaderMiddleDefault from './templates/card-headers/CardHeaderMiddleDefault';
import CardHeaderRightDefault from './templates/card-headers/CardHeaderRightDefault';
import CardHeaderWrapperDefault from './templates/card-headers/CardHeaderWrapperDefault';
import ScheduledAppointments from './templates/card-sections/ScheduledAppointments';
import HeaderDefault from './templates/HeaderDefault';
import NullComponent from './templates/NullComponent';

import './EncounterSegment.scss';

export const EncounterSegmentContext = createContext();

function EncounterSegment({
  encounter,
  refetchTabData,
  className,
  style,
  Header = HeaderDefault,
  CardHeaderWrapper = CardHeaderWrapperDefault,
  CardHeaderLeft = CardHeaderLeftDefault,
  CardHeaderMiddle = CardHeaderMiddleDefault,
  CardHeaderRight = CardHeaderRightDefault,
  CardMiddleSection = NullComponent,
  CardLastSection = ScheduledAppointments
}) {
  const { currentStudy } = usePatientInfo();
  const { sitePatientEncounter, encounterType, epochName, encounterEvents } = encounter;
  const studySiteStatus = currentStudy?.studySiteStatus;
  const isPresentEpochs = currentStudy?.isPresentEpochs;
  const isNonProtocol = encounterType === NON_PROTOCOL;

  const isManageEncountersAllowed = useMemo(() => {
    if (userHasRole(ROLE_SYSTEM_ADMINISTRATOR)) {
      return true;
    }
    if (sitePatientEncounter) {
      if (isNonProtocol) {
        return ![CLOSED, CANCELED].includes(studySiteStatus) && userHasAccessTo(MANAGE_PRE_SCREEN_WORK_LIST);
      }
      return studySiteStatus && studySiteStatus !== CLOSED && userHasAccessTo(MANAGE_PROTOCOL_ENCOUNTER_DATA);
    }

    return false;
  }, [sitePatientEncounter, isNonProtocol, studySiteStatus]);

  const lastNotCancelledEncounterEvent = useMemo(
    function() {
      return { ...last(reject(encounterEvents, 'cancelled')) };
    },
    [encounterEvents]
  );

  const lastEncounterEvent = useMemo(
    function() {
      return { ...last(encounterEvents) };
    },
    [encounterEvents]
  );

  const resolvedEpochName = encounterType === SITUATIONAL ? 'Situational' : epochName;

  return (
    <EncounterSegmentContext.Provider
      value={{
        encounter,
        refetchTabData,
        isManageEncountersAllowed,
        lastNotCancelledEncounterEvent,
        lastEncounterEvent
      }}
    >
      <div className={cx('encounter-segment', className)} style={style}>
        <div className="encounter-segment-epoch-information">
          <div className="encounter-segment-epoch-title">{isPresentEpochs && `Epoch: ${resolvedEpochName}`}</div>
          <Header />
        </div>
        <div className="encounter-segment-card-container">
          <CardHeaderWrapper>
            <div className="encounter-segment-card-header">
              <CardHeaderLeft />
              <CardHeaderMiddle />
              <CardHeaderRight />
            </div>
          </CardHeaderWrapper>
          <CardMiddleSection />
          <CardLastSection />
        </div>
      </div>
    </EncounterSegmentContext.Provider>
  );
}

export default memo(EncounterSegment);

export function useEncounterSegment() {
  const context = useContext(EncounterSegmentContext);
  if (context === undefined) {
    throw new Error('useEncounterSegment must be used within a EncounterSegmentContext.Provider');
  }
  return context;
}
